import { compose } from 'ramda';

const createTransformDeep = (condition, transform) => {
  const transformDeep = input =>
    Object.keys(input).reduce((output, key) => {
      const value = input[key];
      if (typeof value === 'object' && value !== null) {
        output[key] = transformDeep(value);
      } else if (condition(key, value)) {
        const newValue = transform(key, value);
        if (newValue !== undefined) output[key] = newValue;
      } else {
        output[key] = value;
      }
      return output;
    }, {});

  return transformDeep;
};

export const transformBoolean = createTransformDeep(
  (key, value) => value === 'true' || value === 'false',
  (key, value) => value === 'true'
);

export const stripTypename = createTransformDeep(
  key => key === '__typename',
  () => undefined
);

// TODO: Per Ankit, an npm package used by the backend doesn't accept `null`.
export const replaceNullWithEmptyString = createTransformDeep(
  (_, value) => value === null,
  () => ''
);

export const transformRemoteInput = compose(
  transformBoolean,
  stripTypename,
  replaceNullWithEmptyString
);

// Useful for creating a copy of a response object b/c they comes back frozen.
// Frozen objects cannot be used as a source of mutation unless cloned.
// https://github.com/apollographql/apollo-client/issues/1909
export const clonePrimitive = value => JSON.parse(JSON.stringify(value));

export const parseResult = key => result => result.data[key];

const withFetchPolicy = fetchPolicy => config => ({
  ...config,
  fetchPolicy,
});

export const networkOnly = withFetchPolicy('network-only');
export const cacheOnly = withFetchPolicy('cache-only');

/**
 * TODO: Temporary utility until we figure out how to handle caching of `null`.
 * TODO: Try using `watchQuery` with `cache-and-network`?
 * When we query for a type and it comes back `null`, that `null` stays even
 * once we make remote mutations and receive a copy of the originally requested
 * object. We're not sure how this situation is meant to be handled yet.
 *
 * This function takes an apollo operation, tries to resolve it against the
 * cache, and, if the result is `null`, repeats the operation but against the
 * network.
 *
 * @param operation The apollo `query` or `mutate` function.
 * @param config The object that would be passed to `operation`.
 * @param dataKey The key on `data` with the expected type in the response.
 */
export const cacheAndNetworkFlat = (operation, config, dataKey) => {
  const request = config => operation(config).then(parseResult(dataKey));

  return request(cacheOnly(config)).then(
    data => data || request(networkOnly(config))
  );
};
